/* eslint-disable no-nested-ternary */
// importer la librairie de react
import React, { useEffect, useState } from "react"
import { Button, Fab, Icon, styled } from "@mui/material"
import { PageConfig, CwComponentConfig } from "../../models/pageConfig"
import DialogService from "../../services/dialog"
import ActionService from "../../services/action"
import UIService from "../../services/UIService"
import { DialogType } from "../../models/dialog"
import { Color } from "../../models/colors"
import { Action } from "../../models/action"
import DynamicParamsService from "../../services/dynamicParams"
import { useTranslation } from "react-i18next"
import StringUtils from "@front-apps/core/utils/stringUtils"

interface State {
    show: boolean
    color?: Color
    clickActions?: Array<Action>
    icon?: string
    disabled?: boolean
    className?: string
}

const CwButton = (
    props: PageConfig<CwComponentConfig.Button> & {
        onClick?: (value: any) => void
    }
): JSX.Element | null => {
    const { config } = props
    // création de toute les variables à partir des config
    const {
        color,
        size,
        backgroundColor,
        backgroundImage,
        variant,
        disabled,
        fieldType,
        className,
        useRefreshListener,
        fab,
        context,
        iconPosition = "start",
    } = config
    const show = typeof config.show === "boolean" ? config.show : true
    const { t } = useTranslation()
    const [text, setText] = useState<string>(config.text)
    const [state, setState] = useState<State>({
        show,
        color,
        clickActions: config.clickActions,
        icon: config.icon,
        disabled,
        className,
    })
    const buttonBackColor = UIService.getColorByName(backgroundColor)
    const buttonBackImage = UIService.getLinearGradByName(backgroundImage)

    /**
     * Fonction d'exec des actions d'initialisation
     */
    const init = () => {
        DynamicParamsService.resolve([config.text], context).then(
            (result) => {
                let resolvedText = config.text
                if (result) {
                    resolvedText = result[0] as string
                }
                setState({
                    show,
                    color,
                    clickActions: config.clickActions,
                    icon: config.icon,
                    disabled,
                    className,
                })
                if (config.pipe)
                    return setText(
                        StringUtils.specialTypeRender(config.pipe, resolvedText)
                    )
                setText(resolvedText)
            },
            (err) => {
                console.error(err)
            }
        )

        // exec les actions d'init
        if (props.actions && props.actions.length)
            ActionService.exec(props.actions, context).then((conf) => {
                // override des attrs de state par ceux de conf
                if (conf?.type === "config") {
                    if (conf.value.text)
                        setText(
                            DynamicParamsService.resolveLang(conf.value.text)
                        )

                    setState({ ...state, ...conf.value })
                }
            })
    }

    useEffect(() => {
        let subId: string | null = null
        // on s'abonne aux changements de route
        if (useRefreshListener)
            subId = DialogService.getInstance().subscribe(
                "pageRefesh",
                DialogType.REFRESH,
                props.name,
                () => {
                    init()
                }
            )

        // unsubscribe
        return () => {
            if (subId) DialogService.getInstance().unsubscribe(subId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        // premier init
        init()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config])

    /**
     * handleClick gére les clics boutons
     */
    const handleClick = () => {
        setState({ ...state, disabled: true })
        // récupère les actions à faire lors du click
        if (state.clickActions) {
            // executer les actions
            ActionService.exec(state.clickActions, context).then(
                (res: any) => {
                    if (props.onClick) {
                        props.onClick(res)
                    }
                    setState({ ...state, disabled: false })
                },
                (err) => {
                    console.error(err)
                }
            )
        } else if (props.onClick) {
            // si pas d'actions on appel la callback avec null
            props.onClick(null)
            setState({ ...state, disabled: false })
        }
    }

    return state.show ? (
        !fab ? (
            <Button
                sx={{
                    color: UIService.getColorByName(state.color),
                    background: buttonBackColor,
                    backgroundImage: buttonBackImage,
                    "&:hover": {
                        opacity: 0.7,
                        backgroundColor: buttonBackColor,
                        backgroundImage: buttonBackImage,
                    },
                }}
                disabled={state.disabled}
                size={size}
                type={fieldType}
                variant={variant}
                className={`${state.className}`}
                onClick={handleClick}
            >
                {state.icon && iconPosition === "start" ? (
                    <Icon sx={{ marginRight: "5px" }}>{state.icon}</Icon>
                ) : null}
                {t(text)}
                {state.icon && iconPosition === "end" ? (
                    <Icon sx={{ marginLeft: "5px" }}>{state.icon}</Icon>
                ) : null}
            </Button>
        ) : (
            <Fab
                className={`${state.className}`}
                variant="extended"
                aria-label="fab button"
                style={{
                    background: buttonBackColor,
                    color: UIService.getColorByName(state.color),
                }}
                onClick={handleClick}
                disabled={state.disabled}
            >
                <Icon> {state.icon} </Icon>
                {t(text)}
            </Fab>
        )
    ) : null
}

export default CwButton
